import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  LinearProgress,
  Divider,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import MDButton from '../../components/MDButton';

/**
 * Admin Panel - Emisyon Faktörleri Yönetimi
 * CSV upload ve manuel faktör ekleme özelliği
 */
const EmissionFactorsAdmin = ({ onFactorsUpdate }) => {
  const [uploadedData, setUploadedData] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [addFactorOpen, setAddFactorOpen] = useState(false);
  const [newFactor, setNewFactor] = useState({
    activity: '',
    factor: '',
    scope: '1',
    category: '',
    source: '',
    notes: '',
    unit: '',
    region: 'custom'
  });

  const fileInputRef = useRef();

  // CSV Template download
  const downloadTemplate = () => {
    const template = `activity,factor,scope,category,source,notes,unit,region
electricity,0.486,2,purchased_energy,Grid Emission Factor,National grid average,kgCO2e/kWh,turkey
natural_gas,2.016,1,stationary_combustion,IPCC 2006,Natural gas combustion,kgCO2e/m3,turkey
diesel,2.687,1,mobile_combustion,IPCC 2006,Diesel fuel for vehicles,kgCO2e/L,turkey`;

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'emission_factors_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // CSV dosyası işleme
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setUploadError('Lütfen CSV dosyası seçin');
      return;
    }

    setIsUploading(true);
    setUploadError('');
    setUploadSuccess('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const parsedData = parseCSV(csvText);
        
        if (parsedData.length === 0) {
          setUploadError('CSV dosyası boş veya geçersiz format');
          setIsUploading(false);
          return;
        }

        setUploadedData(parsedData);
        setPreviewOpen(true);
        setUploadSuccess(`${parsedData.length} emisyon faktörü başarıyla okundu`);
      } catch (error) {
        setUploadError(`CSV işleme hatası: ${error.message}`);
      }
      setIsUploading(false);
    };

    reader.onerror = () => {
      setUploadError('Dosya okuma hatası');
      setIsUploading(false);
    };

    reader.readAsText(file);
  };

  // CSV parsing
  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV dosyası en az başlık ve bir veri satırı içermelidir');
    }

    const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
    const requiredHeaders = ['activity', 'factor', 'scope', 'category', 'source', 'unit'];
    
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    if (missingHeaders.length > 0) {
      throw new Error(`Eksik sütunlar: ${missingHeaders.join(', ')}`);
    }

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length !== headers.length) {
        console.warn(`Satır ${i + 1}: Sütun sayısı eşleşmiyor`);
        continue;
      }

      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index];
      });

      // Veri validasyonu
      if (!row.activity || !row.factor || !row.scope) {
        console.warn(`Satır ${i + 1}: Gerekli alanlar eksik`);
        continue;
      }

      // Factor numeric kontrolü
      const factorValue = parseFloat(row.factor);
      if (isNaN(factorValue) || factorValue < 0) {
        console.warn(`Satır ${i + 1}: Geçersiz faktör değeri`);
        continue;
      }

      row.factor = factorValue;
      row.scope = row.scope.toString();
      row.region = row.region || 'custom';
      
      data.push(row);
    }

    return data;
  };

  // Faktörleri sisteme ekle
  const handleApplyFactors = () => {
    if (!uploadedData || uploadedData.length === 0) {
      setUploadError('Uygulanacak veri bulunamadı');
      return;
    }

    try {
      // Mevcut faktörleri al
      const existingFactors = JSON.parse(localStorage.getItem('customEmissionFactors') || '{}');
      
      // Yeni faktörleri ekle
      uploadedData.forEach(factor => {
        if (!existingFactors[factor.region]) {
          existingFactors[factor.region] = {};
        }
        
        existingFactors[factor.region][factor.activity] = {
          factor: factor.factor,
          scope: factor.scope,
          category: factor.category,
          source: factor.source,
          notes: factor.notes || '',
          unit: factor.unit
        };
      });

      // Local storage'a kaydet
      localStorage.setItem('customEmissionFactors', JSON.stringify(existingFactors));
      
      // Parent component'e bildir
      if (onFactorsUpdate) {
        onFactorsUpdate(existingFactors);
      }

      setUploadSuccess(`${uploadedData.length} emisyon faktörü başarıyla eklendi`);
      setPreviewOpen(false);
      setUploadedData(null);
      
      // File input'u temizle
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      setUploadError(`Faktörler eklenirken hata: ${error.message}`);
    }
  };

  // Manuel faktör ekleme
  const handleAddFactor = () => {
    const validation = validateNewFactor();
    if (!validation.isValid) {
      setUploadError(validation.message);
      return;
    }

    try {
      const existingFactors = JSON.parse(localStorage.getItem('customEmissionFactors') || '{}');
      
      if (!existingFactors[newFactor.region]) {
        existingFactors[newFactor.region] = {};
      }
      
      existingFactors[newFactor.region][newFactor.activity] = {
        factor: parseFloat(newFactor.factor),
        scope: newFactor.scope,
        category: newFactor.category,
        source: newFactor.source,
        notes: newFactor.notes,
        unit: newFactor.unit
      };

      localStorage.setItem('customEmissionFactors', JSON.stringify(existingFactors));
      
      if (onFactorsUpdate) {
        onFactorsUpdate(existingFactors);
      }

      setUploadSuccess('Yeni emisyon faktörü eklendi');
      setAddFactorOpen(false);
      resetNewFactor();
    } catch (error) {
      setUploadError(`Faktör eklenirken hata: ${error.message}`);
    }
  };

  const validateNewFactor = () => {
    if (!newFactor.activity) return { isValid: false, message: 'Aktivite adı gerekli' };
    if (!newFactor.factor || isNaN(parseFloat(newFactor.factor))) return { isValid: false, message: 'Geçerli faktör değeri gerekli' };
    if (!newFactor.scope) return { isValid: false, message: 'Scope seçimi gerekli' };
    if (!newFactor.category) return { isValid: false, message: 'Kategori gerekli' };
    if (!newFactor.source) return { isValid: false, message: 'Kaynak gerekli' };
    if (!newFactor.unit) return { isValid: false, message: 'Birim gerekli' };
    
    return { isValid: true };
  };

  const resetNewFactor = () => {
    setNewFactor({
      activity: '',
      factor: '',
      scope: '1',
      category: '',
      source: '',
      notes: '',
      unit: '',
      region: 'custom'
    });
  };

  // Mevcut custom faktörleri al
  const getCustomFactors = () => {
    try {
      return JSON.parse(localStorage.getItem('customEmissionFactors') || '{}');
    } catch {
      return {};
    }
  };

  const customFactors = getCustomFactors();
  const customFactorsList = Object.entries(customFactors).flatMap(([region, factors]) =>
    Object.entries(factors).map(([activity, data]) => ({
      region,
      activity,
      ...data
    }))
  );

  // Custom faktör silme
  const deleteCustomFactor = (region, activity) => {
    try {
      const factors = getCustomFactors();
      if (factors[region] && factors[region][activity]) {
        delete factors[region][activity];
        
        // Eğer region boş kaldıysa region'ı da sil
        if (Object.keys(factors[region]).length === 0) {
          delete factors[region];
        }
        
        localStorage.setItem('customEmissionFactors', JSON.stringify(factors));
        
        if (onFactorsUpdate) {
          onFactorsUpdate(factors);
        }
        
        setUploadSuccess('Emisyon faktörü silindi');
      }
    } catch (error) {
      setUploadError(`Faktör silinirken hata: ${error.message}`);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="primary">
        ⚙️ Emisyon Faktörleri Yönetimi
      </Typography>
      
      {/* Durum Mesajları */}
      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>
          {uploadError}
        </Alert>
      )}
      {uploadSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadSuccess('')}>
          {uploadSuccess}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* CSV Upload Bölümü */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>📁 CSV Dosyası ile Toplu Ekleme</Typography>
              
              <Box sx={{ mb: 2 }}>
                <MDButton
                  variant="outlined"
                  onClick={downloadTemplate}
                  startIcon={<DownloadIcon />}
                  sx={{ mr: 2, mb: 2 }}
                >
                  Şablon İndir
                </MDButton>
                
                <input
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                />
                
                <MDButton
                  variant="contained"
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={<UploadIcon />}
                  disabled={isUploading}
                >
                  {isUploading ? 'Yükleniyor...' : 'CSV Yükle'}
                </MDButton>
              </Box>

              {isUploading && <LinearProgress sx={{ mb: 2 }} />}

              <Typography variant="body2" color="textSecondary">
                CSV dosyanız şu sütunları içermelidir: activity, factor, scope, category, source, unit
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Manuel Ekleme Bölümü */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>✏️ Manuel Faktör Ekleme</Typography>
              
              <MDButton
                variant="contained"
                color="primary"
                onClick={() => setAddFactorOpen(true)}
                startIcon={<AddIcon />}
                fullWidth
              >
                Yeni Faktör Ekle
              </MDButton>
              
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Tek tek emisyon faktörü eklemek için bu seçeneği kullanın
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Mevcut Custom Faktörler */}
      {customFactorsList.length > 0 && (
        <Paper elevation={2} sx={{ mt: 3, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            📋 Mevcut Özel Faktörler ({customFactorsList.length})
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Aktivite</TableCell>
                  <TableCell>Faktör</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Kategori</TableCell>
                  <TableCell>Bölge</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customFactorsList.map((factor, index) => (
                  <TableRow key={index}>
                    <TableCell>{factor.activity}</TableCell>
                    <TableCell>{factor.factor} {factor.unit}</TableCell>
                    <TableCell>
                      <Chip label={`Scope ${factor.scope}`} size="small" />
                    </TableCell>
                    <TableCell>{factor.category}</TableCell>
                    <TableCell>{factor.region}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => deleteCustomFactor(factor.region, factor.activity)}
                        startIcon={<DeleteIcon />}
                      >
                        Sil
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* CSV Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>📋 CSV Önizleme ve Onay</DialogTitle>
        <DialogContent>
          {uploadedData && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {uploadedData.length} emisyon faktörü okundu. Onaylarsanız sisteme eklenecektir.
              </Alert>
              
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Aktivite</TableCell>
                      <TableCell>Faktör</TableCell>
                      <TableCell>Scope</TableCell>
                      <TableCell>Kategori</TableCell>
                      <TableCell>Kaynak</TableCell>
                      <TableCell>Birim</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadedData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.activity}</TableCell>
                        <TableCell>{row.factor}</TableCell>
                        <TableCell>
                          <Chip label={`Scope ${row.scope}`} size="small" />
                        </TableCell>
                        <TableCell>{row.category}</TableCell>
                        <TableCell>{row.source}</TableCell>
                        <TableCell>{row.unit}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>İptal</Button>
          <MDButton
            variant="contained"
            onClick={handleApplyFactors}
            startIcon={<SaveIcon />}
          >
            Faktörleri Sisteme Ekle
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Manuel Faktör Ekleme Dialog */}
      <Dialog open={addFactorOpen} onClose={() => setAddFactorOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>➕ Yeni Emisyon Faktörü Ekle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Aktivite Adı"
              value={newFactor.activity}
              onChange={(e) => setNewFactor({...newFactor, activity: e.target.value})}
              required
              placeholder="Örn: custom_electricity"
            />
            
            <TextField
              label="Emisyon Faktörü"
              type="number"
              value={newFactor.factor}
              onChange={(e) => setNewFactor({...newFactor, factor: e.target.value})}
              required
              inputProps={{ min: 0, step: 0.001 }}
            />
            
            <TextField
              label="Birim"
              value={newFactor.unit}
              onChange={(e) => setNewFactor({...newFactor, unit: e.target.value})}
              required
              placeholder="Örn: kgCO2e/kWh"
            />
            
            <FormControl required>
              <InputLabel>Scope</InputLabel>
              <Select
                value={newFactor.scope}
                onChange={(e) => setNewFactor({...newFactor, scope: e.target.value})}
                label="Scope"
              >
                <MenuItem value="1">Scope 1 - Doğrudan Emisyonlar</MenuItem>
                <MenuItem value="2">Scope 2 - Enerji Emisyonları</MenuItem>
                <MenuItem value="3">Scope 3 - Dolaylı Emisyonlar</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Kategori"
              value={newFactor.category}
              onChange={(e) => setNewFactor({...newFactor, category: e.target.value})}
              required
              placeholder="Örn: purchased_energy"
            />
            
            <TextField
              label="Kaynak"
              value={newFactor.source}
              onChange={(e) => setNewFactor({...newFactor, source: e.target.value})}
              required
              placeholder="Örn: IPCC 2006 Guidelines"
            />
            
            <TextField
              label="Notlar (Opsiyonel)"
              value={newFactor.notes}
              onChange={(e) => setNewFactor({...newFactor, notes: e.target.value})}
              multiline
              rows={2}
              placeholder="Ek açıklamalar..."
            />
            
            <TextField
              label="Bölge"
              value={newFactor.region}
              onChange={(e) => setNewFactor({...newFactor, region: e.target.value})}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFactorOpen(false)}>İptal</Button>
          <MDButton
            variant="contained"
            onClick={handleAddFactor}
            startIcon={<SaveIcon />}
          >
            Faktörü Ekle
          </MDButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmissionFactorsAdmin;
